/**
 * Single error mapper for the SDK transport.
 *
 * Consolidates the per-tool error handling that used to live in every client
 * (compact, reflex, github, …) into one place, preserving the actionable
 * 401/429 messaging. Reuses the existing `MorphError` type so callers that
 * `instanceof MorphError` keep working.
 */
import { MorphError } from '../tools/utils/resilience.js';

interface ApiErrorBody {
  error?: { message?: string; code?: string; type?: string };
  message?: string;
}

/**
 * Turn a non-OK `Response` into a `MorphError`, extracting the API's error
 * message when present and marking 429/503 as retryable.
 */
export async function toMorphError(response: Response): Promise<MorphError> {
  let message = `Morph API request failed (${response.status})`;
  let code = 'api_error';

  try {
    const body = (await response.json()) as ApiErrorBody;
    message = body.error?.message ?? body.message ?? message;
    code = body.error?.code ?? body.error?.type ?? code;
  } catch {
    // Non-JSON body — keep the status-based default message.
  }

  if (response.status === 401) code = 'authentication_error';
  if (response.status === 429) code = 'rate_limit_exceeded';

  const retryable = response.status === 429 || response.status === 503;
  return new MorphError(message, code, response.status, retryable);
}
